import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Wallet } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Dialog } from '../components/ui/Dialog';
import { Input, Textarea, Label } from '../components/ui/Input';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/Table';
import { PageLoader } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch, ApiError } from '../lib/apiFetch';
import { maskCard } from '../lib/format';
import { useToast } from '../components/ui/Toast';
import { useT } from '../i18n';

interface Method {
  id: number;
  provider_code: string;
  title: string;
  card_number: string | null;
  card_holder: string | null;
  bank_name: string | null;
  instructions: string | null;
  is_active: boolean;
  sort_order: number;
}

interface FormState {
  title: string;
  card_number: string;
  card_holder: string;
  bank_name: string;
  instructions: string;
  sort_order: number;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  card_number: '',
  card_holder: '',
  bank_name: '',
  instructions: '',
  sort_order: 0,
  is_active: true,
};

export function PaymentMethods() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Method | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();

  const list = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => apiFetch<{ methods: Method[] }>('/api/admin/payment-methods'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiFetch(`/api/admin/payment-methods/${id}/toggle`, {
        method: 'POST',
        body: { is_active: isActive },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] }),
    onError: (err) => toast.error((err as ApiError).message ?? t('methods.toggleFailed')),
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/payment-methods/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('methods.deleted'));
      qc.invalidateQueries({ queryKey: ['payment-methods'] });
    },
    onError: (err) => toast.error((err as ApiError).message ?? t('methods.deleteFailed')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>{t('methods.title')}</CardTitle>
            <CardDescription>{t('methods.description')}</CardDescription>
          </CardHeader>
        </Card>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          {t('common.add')}
        </Button>
      </div>

      {list.isLoading ? (
        <PageLoader />
      ) : !list.data || list.data.methods.length === 0 ? (
        <EmptyState
          icon={<Wallet className="h-10 w-10" />}
          title={t('methods.emptyTitle')}
          description={t('methods.emptyDescription')}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>{t('common.title')}</TH>
                  <TH>{t('methods.card')}</TH>
                  <TH>{t('methods.holder')}</TH>
                  <TH>{t('methods.bank')}</TH>
                  <TH>{t('common.status')}</TH>
                  <TH className="text-right">{t('common.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.methods.map((m) => (
                  <TR key={m.id}>
                    <TD className="font-medium">{m.title}</TD>
                    <TD className="font-mono">{maskCard(m.card_number)}</TD>
                    <TD>{m.card_holder ?? '—'}</TD>
                    <TD>{m.bank_name ?? '—'}</TD>
                    <TD>
                      <Badge variant={m.is_active ? 'success' : 'muted'}>
                        {m.is_active ? t('methods.statusOn') : t('methods.statusOff')}
                      </Badge>
                    </TD>
                    <TD className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setEditing(m)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          loading={toggle.isPending && toggle.variables?.id === m.id}
                          onClick={() => toggle.mutate({ id: m.id, isActive: !m.is_active })}
                        >
                          {m.is_active ? t('common.disable') : t('common.enable')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm(t('methods.confirmDelete', { title: m.title }))) {
                              remove.mutate(m.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={creating} onClose={() => setCreating(false)} title={t('methods.newTitle')} size="lg">
        {/* key='new' — гарантирует переинициализацию state между «Добавить» и «Редактировать» */}
        {creating && (
          <MethodForm
            key="new"
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              qc.invalidateQueries({ queryKey: ['payment-methods'] });
              toast.success(t('methods.created'));
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={t('methods.editTitle', { title: editing?.title ?? '' })}
        size="lg"
      >
        {editing && (
          <MethodForm
            key={`edit-${editing.id}`}
            existing={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              qc.invalidateQueries({ queryKey: ['payment-methods'] });
              toast.success(t('methods.updated'));
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function MethodForm({
  existing,
  onCancel,
  onSaved,
}: {
  existing?: Method;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!existing;
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState<FormState>(() =>
    existing
      ? {
          title: existing.title,
          card_number: existing.card_number ?? '',
          card_holder: existing.card_holder ?? '',
          bank_name: existing.bank_name ?? '',
          instructions: existing.instructions ?? '',
          sort_order: existing.sort_order,
          is_active: existing.is_active,
        }
      : EMPTY_FORM,
  );

  // Нормализация номера карты: убираем пробелы/дефисы, оставляем цифры.
  const normalizeCardNumber = (raw: string): string => raw.replace(/[\s-]+/g, '');

  // Валидация: 13-19 цифр (стандарт UzCard/Humo/Visa/Mastercard).
  const isValidCardNumber = (raw: string): boolean => {
    if (!raw) return true; // карта не обязательна
    const digits = normalizeCardNumber(raw);
    return /^\d{13,19}$/.test(digits);
  };

  const submit = useMutation({
    mutationFn: () => {
      // PATCH: шлём null для очищенных полей, undefined игнорируется.
      const trim = (s: string) => s.trim();
      const body: Record<string, unknown> = {
        title: trim(form.title),
        card_number: trim(form.card_number) ? normalizeCardNumber(trim(form.card_number)) : null,
        card_holder: trim(form.card_holder) || null,
        bank_name: trim(form.bank_name) || null,
        instructions: trim(form.instructions) || null,
        sort_order: form.sort_order,
        is_active: form.is_active,
      };
      if (isEdit && existing) {
        return apiFetch(`/api/admin/payment-methods/${existing.id}`, { method: 'PATCH', body });
      }
      return apiFetch('/api/admin/payment-methods', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) => toast.error((err as ApiError).message ?? t('methods.saveFailed')),
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const titleOk = form.title.trim().length > 0;
  const cardOk = isValidCardNumber(form.card_number);
  const valid = titleOk && cardOk;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) submit.mutate();
      }}
      className="space-y-3"
    >
      <div>
        <Label>{t('methods.formTitle')}</Label>
        <Input
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          className="mt-1"
          placeholder={t('methods.formTitlePlaceholder')}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>{t('methods.formCard')}</Label>
          <Input
            value={form.card_number}
            onChange={(e) => set('card_number', e.target.value)}
            className={'mt-1 font-mono' + (form.card_number && !cardOk ? ' border-red-400' : '')}
            placeholder="9860 1234 5678 9012"
            inputMode="numeric"
          />
          {form.card_number && !cardOk && (
            <p className="mt-1 text-xs text-red-600">{t('methods.formCardError')}</p>
          )}
        </div>
        <div>
          <Label>{t('methods.formHolder')}</Label>
          <Input
            value={form.card_holder}
            onChange={(e) => set('card_holder', e.target.value)}
            className="mt-1"
            placeholder="IVAN IVANOV"
          />
        </div>
        <div>
          <Label>{t('methods.formBank')}</Label>
          <Input
            value={form.bank_name}
            onChange={(e) => set('bank_name', e.target.value)}
            className="mt-1"
            placeholder={t('methods.formBankPlaceholder')}
          />
        </div>
        <div>
          <Label>{t('methods.formSortOrder')}</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={(e) => set('sort_order', Number(e.target.value) || 0)}
            className="mt-1"
          />
        </div>
      </div>
      <div>
        <Label>{t('methods.formInstructions')}</Label>
        <Textarea
          value={form.instructions}
          onChange={(e) => set('instructions', e.target.value)}
          className="mt-1"
          rows={3}
          placeholder={t('methods.formInstructionsPlaceholder')}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => set('is_active', e.target.checked)}
        />
        {t('methods.formActive')}
      </label>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={submit.isPending} disabled={!valid}>
          {isEdit ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  );
}
